import { divInt, sub } from "../shared/fixed";
import type { MatchRules } from "./ability";
import { LAST_CHANCE_PERCENT } from "./combat";
import type { SimEvent } from "./events";
import { FIGHTER, PHASE, type FighterState, type MatchState } from "./state";

/**
 * Решающий удар как в Tekken (Джон 05.11.2026: «Слабенько. Не как в тэйкен»; описание — консультант): мир сам замедляется
 * в напряжённый миг ещё до попадания — удар, урон которого добьёт соперника, входит в активные кадры рядом с ним, или у
 * обоих мало здоровья и оба бьют. Попадёт или нет — неизвестно: промах — замедление кончается, бой идёт дальше. Не чаще
 * раза в раунд. Мир идёт в SLOW_RATE раз медленнее: обрабатывается каждый SLOW_RATE-й кадр, нажатия между ними копятся
 * и выходят на обработанном кадре. Всё в снимке мира — онлайн у обоих одинаково, откат не ломается.
 */
export const SLOW_RATE = 3;
/** Обработанных кадров мира в замедлении: ~1,2 с настоящего времени. */
const SLOW_FRAMES = 24;
/** Нокаут в замедлении — падение ещё столько обработанных кадров замедленно. */
const FALL_FRAMES = 22;
/** Рядом — соперник не дальше стольких пикселей сверх зоны удара. */
const REACH_SLACK = 30;
/** Сбитый в воздухе — падение замедленно до касания пола (и подскока) и ещё столько обработанных кадров: тело ложится. */
const LAND_FRAMES = 8;
/** Но не дольше стольких кадров нокаута: тело, застрявшее в воздухе, замедление не держит. */
const FALL_LIMIT = 45;

/** Мир сейчас замедлен: идёт решающий удар или стоит между обработанными кадрами. */
export const isSlowed = (state: MatchState): boolean => state.slowmo > 0 || state.slowTick !== 0;

/** Шаг мира в замедлении: true — этот кадр мир стоит (нажатия копятся), false — обработать с накопленными нажатиями. */
export function slowFrame(state: MatchState, inputs: [number, number]): boolean {
  if (state.slowmo <= 0) return false;
  state.slowBits[0] = (state.slowBits[0] ?? 0) | inputs[0];
  state.slowBits[1] = (state.slowBits[1] ?? 0) | inputs[1];
  state.slowTick = (state.slowTick + 1) % SLOW_RATE;
  if (state.slowTick !== 0) return true;
  inputs[0] = state.slowBits[0];
  inputs[1] = state.slowBits[1];
  state.slowBits[0] = 0;
  state.slowBits[1] = 0;
  state.slowmo--;
  return false;
}

/** После шага мира: запустить замедление в напряжённый миг или продлить его на падение после нокаута. */
export function checkDecisive(state: MatchState, rules: MatchRules, events: SimEvent[]): void {
  // нокаут в замедлении — падение тоже замедленно (slowmoDone 2 — продлено), летящее тело — до касания пола
  if (state.slowmo > 0 && state.phase === PHASE.ko) {
    if (state.slowmoDone === 1) {
      state.slowmo = Math.max(state.slowmo, FALL_FRAMES);
      state.slowmoDone = 2;
    }
    const falling = state.fighters.some((fighter) => fighter.health <= 0 && fighter.y < 0);
    if (falling && state.slowmo < LAND_FRAMES && state.phaseFrame < FALL_LIMIT) state.slowmo = LAND_FRAMES;
  }
  if (state.slowmoDone || state.slowmo > 0 || state.phase !== PHASE.fight || state.cinematic >= 0) return;
  const [a, b] = state.fighters;
  const both = low(a, rules, 0) && low(b, rules, 1) && a.state === FIGHTER.ability && b.state === FIGHTER.ability;
  for (const side of [0, 1] as const) {
    if (!both && !lethal(state, rules, side)) continue;
    state.slowmo = SLOW_FRAMES;
    state.slowTick = 0;
    state.slowmoDone = 1;
    state.slowmoBy = side;
    events.push({
      kind: "action",
      frame: state.frame,
      who: side,
      x: Math.round(state.fighters[side].x / 256),
      y: -100,
      strength: 2,
      fx: "decisive/start",
    });
    return;
  }
}

const low = (fighter: FighterState, rules: MatchRules, side: 0 | 1): boolean =>
  fighter.health * 100 <= rules.heroes[side].health * LAST_CHANCE_PERCENT;

/** Удар бойца side входит в активные кадры, соперник рядом, и урона хватит, чтобы закончить раунд. */
function lethal(state: MatchState, rules: MatchRules, side: 0 | 1): boolean {
  const attacker = state.fighters[side];
  if (attacker.state !== FIGHTER.ability) return false;
  const ability = rules.kits[side][attacker.ability];
  const hit = ability?.hit;
  if (!ability || !hit || attacker.abilityFrame !== ability.startup) return false;
  const defender = state.fighters[side === 0 ? 1 : 0];
  if (defender.health <= 0 || hit.damage < defender.health) return false;
  const reach = ability.hitbox ? ability.hitbox.x + ability.hitbox.w + REACH_SLACK : 0;
  if (ability.projectile) return false; // снаряд летит — миг не здесь
  return Math.abs(defender.x - attacker.x) <= sub(reach) + divInt(sub(rules.heroes[side].width), 2);
}
